import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Snackbar } from '@mui/material';
import Header from '../../components/Header';
import DocumentView from '../../components/DocumentView/DocumentView';
import ReprocessInPlaceModal from '../../components/Documents/ReprocessInPlaceModal';
import Spinner from '../../components/Spinner';
import { useApi } from '../../hooks/useApi';
import { getIdTokenClaims } from '../../common/auth';
import { loadDocumentFicha } from '../../utils/loadDocumentFicha';
import { fetchCanonicalDocumentRecord } from '../../utils/documentRedirect';
import { buildDocumentByIdQuery } from '../../utils/documentDetailQuery';
import { useArcaFeature } from '../../hooks/useArcaFeature';
import { loadReprocessContext } from '../../utils/reprocess';
import { reprocessDocumentAs, reprocessToastMessage } from '../../utils/uploadReprocess';
import {
  inPlaceReprocessToastMessage,
  isDocumentReprocessing,
  isInPlaceReprocessable,
  runInPlaceReprocess,
} from '../../utils/documentReprocess';
import '../../components/DocumentView/DocumentView.css';
import './DocumentDetailPage.css';

export default function DocumentDetailPage() {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const { callApi } = useApi();
  const [ficha, setFicha] = useState(null);
  const [documentRecord, setDocumentRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [reprocessDocTypes, setReprocessDocTypes] = useState([]);
  const [toast, setToast] = useState(null);
  const [reprocessModalOpen, setReprocessModalOpen] = useState(false);
  const [reprocessLoading, setReprocessLoading] = useState(false);
  const [isReprocessing, setIsReprocessing] = useState(false);
  const { enabled: arcaEnabled, canConstatar } = useArcaFeature();

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const ctx = await loadReprocessContext(callApi, getIdTokenClaims);
      setCanEdit(ctx.canEdit);
      setReprocessDocTypes(ctx.reprocessDocTypes || []);
      const data = await loadDocumentFicha(callApi, documentId, { canEdit: ctx.canEdit });
      const record = await fetchCanonicalDocumentRecord(
        callApi,
        buildDocumentByIdQuery,
        documentId,
      );
      setFicha(data);
      setDocumentRecord(record);
    } catch (err) {
      if (!silent) {
        setError(err?.message || 'No se pudo cargar el documento');
        setFicha(null);
        setDocumentRecord(null);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [callApi, documentId]);

  const reprocessSource = documentRecord || ficha;
  const reprocessing = isReprocessing || isDocumentReprocessing(reprocessSource);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!documentId || !reprocessing || isReprocessing) return undefined;
    const timer = setInterval(() => {
      load({ silent: true });
    }, 3000);
    return () => clearInterval(timer);
  }, [documentId, reprocessing, isReprocessing, load]);

  const handleClose = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/upload');
    }
  };

  const handleClassify = async (targetDocType) => {
    if (!documentId || !targetDocType) return;
    try {
      const result = await reprocessDocumentAs(callApi, documentId, targetDocType);
      setToast({
        severity: result?.status === 'QUEUED' ? 'success' : 'info',
        message: reprocessToastMessage(result, targetDocType),
      });
      if (result?.status === 'QUEUED') {
        const newId = result?.redirect_target_document_id;
        if (newId && newId !== documentId) {
          navigate(`/documents/${newId}`, { replace: true });
        } else {
          await load();
        }
      }
    } catch (err) {
      setToast({
        severity: 'error',
        message: err?.message || 'Error al clasificar',
      });
    }
  };

  const executeInPlaceReprocess = async () => {
    if (!documentId) return;
    setReprocessLoading(true);
    setIsReprocessing(true);
    try {
      const result = await runInPlaceReprocess(callApi, documentId, {
        onTick: () => load({ silent: true }),
      });
      setReprocessModalOpen(false);
      setToast({
        severity: result?.status === 'QUEUED' ? 'success' : 'error',
        message: inPlaceReprocessToastMessage(result),
      });
      if (result?.status === 'QUEUED') {
        await load();
      }
    } catch (err) {
      setToast({
        severity: 'error',
        message: err?.message || 'Error al reprocesar documento',
      });
    } finally {
      setReprocessLoading(false);
      setIsReprocessing(false);
    }
  };

  const reprocessable = canEdit
    && isInPlaceReprocessable(reprocessSource, { canEdit })
    && !reprocessing;

  return (
    <div className="document-detail-page">
      <Header />
      <main className="document-detail-main">
        {loading && (
          <div className="document-detail-loading">
            <Spinner />
            <p>Cargando comprobante…</p>
          </div>
        )}
        {!loading && error && (
          <Alert severity="error" className="document-detail-error">
            {error}
          </Alert>
        )}
        {!loading && ficha && (
          <div className="document-detail-shell">
            <DocumentView
              ficha={ficha}
              onClose={handleClose}
              onClassify={handleClassify}
              reprocessDocTypes={reprocessDocTypes}
              onFichaChange={setFicha}
              arcaEnabled={arcaEnabled}
              canConstatar={canConstatar}
              reprocessable={reprocessable}
              onReprocess={() => setReprocessModalOpen(true)}
              reprocessLoading={reprocessLoading}
              isReprocessing={reprocessing}
            />
          </div>
        )}
      </main>

      <ReprocessInPlaceModal
        isOpen={reprocessModalOpen}
        filename={ficha?.filename}
        loading={reprocessLoading}
        onConfirm={executeInPlaceReprocess}
        onCancel={() => {
          if (!reprocessLoading) setReprocessModalOpen(false);
        }}
      />

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast?.severity || 'info'} onClose={() => setToast(null)}>
          {toast?.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
